// frontend/src/hooks/usePermission.js
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook to check user permissions
 * @returns {Object} Permission checking functions
 */
export const usePermission = () => {
  const { user } = useAuth();
  
  /**
   * Check if the user has a specific permission
   * @param {string} application - The application name (e.g., 'API-management-portal')
   * @param {string} permission - The permission to check
   * @returns {boolean} - Whether the user has the permission
   */
  const hasPermission = (application, permission) => {
    if (!user || !user.permissions) {
      return false;
    }

    // Full-access roles have all permissions
    if (['admin', 'company_admin'].includes(user.role)) {
      return true;
    }

    // Get all permissions from default and custom
    const permissions = user.permissions || { default: [] };
    const allPermissions = [
      ...(permissions.default || []),
      ...(permissions.custom || [])
    ];

    // Check for permission with application prefix or just the permission
    const fullPermission = application ? `${application}.${permission}` : permission;
    return allPermissions.includes(fullPermission) || allPermissions.includes(permission);
  };
  
  /**
   * Check if the user has any of the specified permissions
   * @param {string} application - The application name
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean} - Whether the user has any of the permissions
   */
  const hasAnyPermission = (application, permissions) => {
    return permissions.some(permission => hasPermission(application, permission));
  };
  
  /**
   * Check if the user has all of the specified permissions
   * @param {string} application - The application name
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean} - Whether the user has all of the permissions
   */
  const hasAllPermissions = (application, permissions) => {
    return permissions.every(permission => hasPermission(application, permission));
  };
  
  /**
   * Get all permissions for a specific application
   * @param {string} application - The application name
   * @returns {string[]} - Array of permissions the user has for the application
   */
  const getPermissionsForApplication = (application) => {
    if (!user || !user.permissions) {
      return [];
    }

    // Admin role has all permissions - would need to return a complete list
    if (user.role === 'admin') {
      // For admin, return a predefined complete list of permissions
      // This is just an example - customize based on your needs
      return [
        'login',
        'devices.view', 'devices.create', 'devices.edit', 'devices.delete',
        'locations.view', 'locations.create', 'locations.edit', 'locations.delete',
        'tickets.view', 'tickets.create', 'tickets.edit', 'tickets.delete',
        'users.view', 'users.create', 'users.edit', 'users.delete', 'users.permissions.manage',
        'company.view', 'company.edit',
        'files.view', 'files.upload', 'files.download', 'files.delete', 'files.share',
        'reports.create', 'reports.view', 'reports.export'
      ];
    }

    // Get all permissions from default and custom
    const permissions = user.permissions || { default: [] };
    const allPermissions = [
      ...(permissions.default || []),
      ...(permissions.custom || [])
    ];

    // For non-admin users, extract permissions for the application
    const applicationPermissions = [];

    allPermissions.forEach(perm => {
      // Handle permissions that match the application prefix
      if (perm.startsWith(`${application}.`)) {
        applicationPermissions.push(perm.split('.').slice(1).join('.'));
      }
      // Handle permissions without application prefix
      else if (!perm.includes('.')) {
        applicationPermissions.push(perm);
      }
    });

    return [...new Set(applicationPermissions)]; // Remove duplicates
  };
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getPermissionsForApplication
  };
};

export default usePermission;